import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import BillingTabBar from '@/pages/billing/components/BillingTabBar';
import ShortcutBar from '@/components/feature/ShortcutBar';
import { useToast } from '@/contexts/ToastContext';
//import { mockCompany } from '@/mocks/masters';
import POHeaderBar from './components/POHeaderBar';
import POItemsTable from './components/POItemsTable';
import POSummaryPanel from './components/POSummaryPanel';
import POSupplierPanel from './components/POSupplierPanel';
import POInfoPanel from './components/POInfoPanel';
import { apiCreatePO } from '@/api/purchaseOrderApi';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { useCompanyState } from '@/hooks/useCompanyState';
import { useBillingTabStore } from '@/stores/billingTabStore';
import { getNextDocumentNumber } from '@/services/documentNumberService';

import {
  createItem,
  filterItems,
  mapApiToItem,
  type ItemResponse,
} from '@/api/item.api';
import ItemForm, { ItemFormData } from '@/pages/masters/items/components/ItemForm';

interface ItemLookup {
  id: string;
  name: string;
  code: string;
  hsnCode: string;
  taxRate: number;
  purchaseRate: number;
  saleRate: number;
  unit: string;
  unitId: string;
}

export interface POSupplier {
  id: string;
  name: string;
  gstin: string;
  phone: string;
  stateCode: string;
  address: string;
  city: string;
}

export interface POItem {
  id: string;
  itemId: string;
  itemName: string;
  barcode: string;
  size: string;
  hsnCode: string;
  gstRate: number;
  group: string;
  brand: string;
  articleNo: string;
  qty: number;
  purRate: number;
  lastPrice: number | null;
  lastSupplier: string;
  lastDate: string;
  amount: number;
  unitId?: string;
  stock?: number;
  /** Item's stock unit code; qty and purRate are in enteredUnitId when set */
  stockUnit?: string;
  enteredUnitId?: string | null;
}

export type POPriority = 'Normal' | 'High' | 'Urgent' | 'Critical';

export interface POFormDraft {
  supplier: POSupplier | null;
  poDate: string;
  expectedDelivery: string;
  items: POItem[];
  priority: POPriority;
  billingAddress: string;
  termsConditions: string;
  deliveryAddress: string;
  paymentTerms: string;
  narration: string;
}

const emptyPOItem = (): POItem => ({
  id: crypto.randomUUID(),
  itemId: '',
  itemName: '',
  barcode: '',
  size: '',
  hsnCode: '',
  gstRate: 0,
  group: '',
  brand: '',
  articleNo: '',
  qty: 1,
  purRate: 0,
  lastPrice: null,
  lastSupplier: '',
  lastDate: '',
  amount: 0,
});

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mapPriority(p: POPriority): string {
  const map: Record<POPriority, string> = {
    Normal: 'NORMAL', High: 'HIGH', Urgent: 'URGENT', Critical: 'URGENT',
  };
  return map[p];
}

const createEmptyPOFormDraft = (): POFormDraft => ({
  supplier: null,
  poDate: todayStr(),
  expectedDelivery: '',
  items: [emptyPOItem()],
  priority: 'Normal',
  billingAddress: '',
  termsConditions: '',
  deliveryAddress: '',
  paymentTerms: '30 Days',
  narration: '',
});

const normalizeDraft = (draft?: Partial<POFormDraft> | null): POFormDraft => {
  const base = createEmptyPOFormDraft();
  if (!draft) return base;
  return {
    ...base,
    ...draft,
    items: draft.items && draft.items.length > 0 ? draft.items : base.items,
  };
};

const resetForm = (
  setSupplier: (v: null) => void,
  setPoDate: (v: string) => void,
  setExpectedDelivery: (v: string) => void,
  setItems: (v: POItem[]) => void,
  setPriority: (v: POPriority) => void,
  setBillingAddress: (v: string) => void,
  setTermsConditions: (v: string) => void,
  setDeliveryAddress: (v: string) => void,
  setPaymentTerms: (v: string) => void,
  setNarration: (v: string) => void,
) => {
  setSupplier(null);
  setPoDate(todayStr());
  setExpectedDelivery('');
  setItems([emptyPOItem()]);
  setPriority('Normal');
  setBillingAddress('');
  setTermsConditions('');
  setDeliveryAddress('');
  setPaymentTerms('30 Days');
  setNarration('');
};

function focusCell(tableEl: HTMLElement | null, rowIdx: number, col: string) {
  if (!tableEl) return;
  const cell = tableEl.querySelector(
    `[data-row="${rowIdx}"][data-col="${col}"]`,
  ) as HTMLElement | null;
  if (!cell) return;
  if (cell.tagName === 'INPUT') {
    (cell as HTMLInputElement).focus();
    return;
  }
  const inner = cell.querySelector('input') as HTMLInputElement | null;
  inner?.focus();
}

export default function PurchaseOrderNewPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { tabs, activeTabId, addTab, setActiveTab, closeTab, setPODraft } =
    useBillingTabStore();
  const hydratingTabRef = useRef(false);
  const initTabRef = useRef(false);

  // ── Init: mirrors the sales invoice pattern exactly ────────────────────────────────
  // On browser refresh: Zustand resets to [] automatically → 1 fresh tab added
  // On navigate to page (first time): tabs=[] → 1 tab added
  // On navigate away + navigate back: Zustand still has draft tabs → nothing added,
  //   existing drafts are preserved so the user can continue working
  useEffect(() => {
    if (initTabRef.current) return;
    initTabRef.current = true;
    if (tabs.length === 0) {
      addTab('PURCHASE');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [supplier, setSupplier] = useState<POSupplier | null>(null);
  const [poDate, setPoDate] = useState(todayStr());
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [poNumber, setPoNumber] = useState('');

  const [items, setItems] = useState<POItem[]>([emptyPOItem()]);
  const tableRef = useRef<HTMLDivElement>(null);

  const [priority, setPriority] = useState<POPriority>('Normal');
  const [billingAddress, setBillingAddress] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('30 Days');
  const [narration, setNarration] = useState('');
  const [saving, setSaving] = useState(false);
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);

  // Fetch next PO number dynamically based on warehouse prefix when creating new
  useEffect(() => {
    async function fetchNextNumber() {
      try {
        const res = await getNextDocumentNumber('PURCHASE_ORDER', selectedWarehouseId);
        if (res?.data?.nextNumber) {
          setPoNumber(res.data.nextNumber);
        }
      } catch (err) {
        console.warn('Failed to fetch next PO number:', err);
      }
    }
    fetchNextNumber();
  }, [selectedWarehouseId]);

  const activePurchaseTabId = tabs.some(
    (t) => t.id === activeTabId && t.voucherType === 'PURCHASE',
  )
    ? activeTabId
    : (tabs.find((t) => t.voucherType === 'PURCHASE')?.id ?? null);

  const [itemLookup, setItemLookup] = useState<ItemLookup[]>([]);

  useEffect(() => {
    if (!activePurchaseTabId) return;
    const tab = tabs.find((t) => t.id === activePurchaseTabId);
    if (!tab) return;

    hydratingTabRef.current = true;
    const draft = tab.poDraft ?? createEmptyPOFormDraft();

    // Automatically set the draft on the tab if it was missing
    if (!tab.poDraft) setPODraft(activePurchaseTabId, draft);
    setSupplier(draft.supplier);
    setPoDate(draft.poDate);
    setExpectedDelivery(draft.expectedDelivery);
    setItems(draft.items.length > 0 ? draft.items : [emptyPOItem()]);
    setPriority(draft.priority);
    setBillingAddress(draft.billingAddress);
    setTermsConditions(draft.termsConditions);
    setDeliveryAddress(draft.deliveryAddress);
    setPaymentTerms(draft.paymentTerms);
    setNarration(draft.narration);
  }, [activePurchaseTabId]);

  useEffect(() => {
    if (!activePurchaseTabId) return;
    if (hydratingTabRef.current) {
      hydratingTabRef.current = false;
      return;
    }
    setPODraft(activePurchaseTabId, {
      supplier,
      poDate,
      expectedDelivery,
      items,
      priority,
      billingAddress,
      termsConditions,
      deliveryAddress,
      paymentTerms,
      narration,
    });
  }, [
    activePurchaseTabId,
    supplier,
    poDate,
    expectedDelivery,
    items,
    priority,
    billingAddress,
    termsConditions,
    deliveryAddress,
    paymentTerms,
    narration,
  ]);

  // Drafts live in memory only (draftsRef). Zustand resets on refresh → one fresh tab.
  // No sessionStorage needed — data persists for the life of the Zustand store (current page visit).

  useEffect(() => {
    let mounted = true;

    void (async () => {
      try {
        const result = await filterItems({});

        if (!mounted) return;

        setItemLookup(
          (result.data ?? []).map((row: ItemResponse) => {
            const item = mapApiToItem(row);

            return {
              id: item.id,
              name: item.name,
              code: item.code,
              hsnCode: item.hsnCode,
              taxRate: item.taxRate,
              purchaseRate: item.purchaseRate,
              saleRate: item.saleRate,
              unit: item.unitName || 'Pcs',
              unitId: item.unitId,
            };
          }),
        );
      } catch {
        setItemLookup([]);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const companyStateCode = useCompanyState();
  const gstType: 'CGST_SGST' | 'IGST' =
    supplier?.stateCode &&
    companyStateCode &&
    supplier.stateCode === companyStateCode
      ? 'CGST_SGST'
      : 'IGST';

  const subtotal = items.reduce((s, i) => s + i.amount, 0);
  const totalQty = items.reduce((s, i) => s + (i.qty || 0), 0);
  const totalItems = items.filter((i) => i.itemName).length;

  const gstBreakdown = items
    .filter((i) => i.itemName && i.amount > 0)
    .reduce(
      (acc, i) => {
        const taxable = i.amount / (1 + i.gstRate / 100);
        const gstAmt = i.amount - taxable;
        if (gstType === 'CGST_SGST') {
          acc.cgst += gstAmt / 2;
          acc.sgst += gstAmt / 2;
        } else {
          acc.igst += gstAmt;
        }
        return acc;
      },
      { cgst: 0, sgst: 0, igst: 0 },
    );

  const totalGST = gstBreakdown.cgst + gstBreakdown.sgst + gstBreakdown.igst;

  const grandTotal = subtotal + totalGST;

  const handleSupplierSelect = useCallback((s: POSupplier | null) => {
    setSupplier(s);
    if (s) {
      setBillingAddress(s.address);
      setDeliveryAddress(s.address);
    }
  }, []);

  const addRow = useCallback(
    () => setItems((prev) => [...prev, emptyPOItem()]),
    [],
  );

  const updateItem = useCallback((id: string, patch: Partial<POItem>) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const updated = { ...it, ...patch };
        updated.amount = Math.round(updated.qty * updated.purRate * 100) / 100;
        return updated;
      }),
    );
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) =>
      prev.length > 1 ? prev.filter((i) => i.id !== id) : prev,
    );
  }, []);

  const persistCurrentDraft = useCallback(() => {
    if (!activePurchaseTabId) return;
    setPODraft(activePurchaseTabId, {
      supplier,
      poDate,
      expectedDelivery,
      items,
      priority,
      billingAddress,
      termsConditions,
      deliveryAddress,
      paymentTerms,
      narration,
    });
  }, [
    activePurchaseTabId,
    supplier,
    poDate,
    expectedDelivery,
    items,
    priority,
    billingAddress,
    termsConditions,
    deliveryAddress,
    paymentTerms,
    narration,
  ]);

  const handleSelectTab = useCallback(
    (tabId: string) => {
      persistCurrentDraft();
      setActiveTab(tabId);
    },
    [persistCurrentDraft, setActiveTab],
  );

  const handleNewTab = useCallback(() => {
    persistCurrentDraft();
    addTab('PURCHASE');
  }, [addTab, persistCurrentDraft]);

  const handleCloseTab = useCallback(
    (tabId: string, isLastVisibleTab: boolean) => {
      if (tabId === activePurchaseTabId) {
        persistCurrentDraft();
      }
      closeTab(tabId);
      if (isLastVisibleTab) {
        navigate('/purchase/orders');
      }
    },
    [activePurchaseTabId, closeTab, navigate, persistCurrentDraft],
  );

  // ── Handle Add New Item from Dropdown ────────────────────────────────────
  // ── Handle Add New Item from Dropdown ────────────────────────────────────
  const handleAddNewItem = useCallback(
    (query: string, rowId: string) => {
      const rowIdx = items.findIndex((item) => item.id === rowId);

      console.log('🆕 Adding new item:', query, 'to row:', rowId);

      // ✅ UPDATE item with the new name
      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== rowId) return item;
          return {
            ...item,
            itemId: '',
            itemName: query, // ✅ SET THE NAME
            // Preserve other fields
          };
        }),
      );

      // ✅ Focus on size field after adding
      if (rowIdx !== -1) {
        setTimeout(() => {
          focusCell(tableRef.current, rowIdx, 'size');
        }, 50);
      }
    },
    [items],
  );

  // ── Resolve New Items (Auto-create on Save) ─────────────────────────────
  const resolveNewItems = useCallback(async () => {
    const resolvedItems = await Promise.all(
      items.map(async (item) => {
        // ✅ Skip if already has itemId OR no itemName
        if (item.itemId || !item.itemName) return item;

        console.log('🆕 Creating new item from PO:', {
          name: item.itemName,
          size: item.size,
          hsnCode: item.hsnCode,
          gstRate: item.gstRate,
          group: item.group,
          brand: item.brand,
          articleNo: item.articleNo,
          purRate: item.purRate,
          qty: item.qty,
          warehouseId: selectedWarehouseId,
        });

        try {
          // ✅ Create item with ALL fields from PO
          const created = await createItem({
            name: item.itemName,
            taxRate: item.gstRate || 18,
            purchaseRate: item.purRate || 0,
            saleRate: item.purRate || 0, // Sale rate same as purchase for new item
            minStockLevel: 5,
            isActive: true,
            warehouseId: selectedWarehouseId || '',
            openingStock: 0,
            sizeColor: item.size || undefined,
            brand: item.brand || undefined,
            articleNo: item.articleNo || undefined,
            hsnCode: item.hsnCode || undefined,
            categoryName: item.group || undefined,
            procurementType: 'BUY', // first seen on a purchase order: a bought-in item
            // The same unit the PO records on this line (a picked unit first); the server links a
            // unit name to the unit master (Kg / kgs → Kg)
            unitId: item.unitId || item.enteredUnitId || undefined,
            unitName: item.stockUnit || 'Pcs',
          });

          if (created.data) {
            const mapped = mapApiToItem(created.data);

            console.log('✅ Item created successfully:', mapped);

            // ✅ Add to local lookup for next times
            setItemLookup((prev) => [
              ...prev.filter((x) => x.id !== mapped.id),
              {
                id: mapped.id,
                name: mapped.name,
                code: mapped.code,
                hsnCode: mapped.hsnCode,
                taxRate: mapped.taxRate,
                purchaseRate: mapped.purchaseRate,
                saleRate: mapped.saleRate,
                unit: mapped.unitName || 'Pcs',
                unitId: mapped.unitId,
              },
            ]);

            return {
              ...item,
              itemId: mapped.id,
            };
          }
        } catch (err) {
          console.error('❌ Failed to auto-create item:', item.itemName, err);
          toast.error(`Failed to create item: ${item.itemName}`);
        }
        return item;
      }),
    );

    setItems(resolvedItems);
    return resolvedItems;
  }, [items, selectedWarehouseId, toast]);

  // ✅ FIX: buildPayload is a plain object builder — NOT invoked as buildPayload()()
  // ✅ FIX: removed unitId filter — PO backend doesn't require unitId
  // ── Build Payload for API ───────────────────────────────────────────────
  const buildPayload = useCallback(
    (currentItems: POItem[]) => {
      const validItems = currentItems.filter(
        (i) => (i.itemName || i.itemId) && i.qty > 0 && i.purRate > 0,
      );

      return {
        poNumber,
        supplierId: supplier!.id,
        date: poDate,
        expectedDelivery: expectedDelivery || undefined,
        warehouseId: selectedWarehouseId,
        priority: mapPriority(priority),
        billingAddress: billingAddress || undefined,
        deliveryAddress: deliveryAddress || undefined,
        paymentTerms: paymentTerms || undefined,
        termsConditions: termsConditions || undefined,
        notes: narration || undefined,
        totalAmount: grandTotal || 0,
        items: validItems.map((i) => ({
          // ✅ If itemId exists, use it; otherwise send itemName (backend will handle)
          itemId: i.itemId || undefined,
          itemName: i.itemName,
          hsnCode: i.hsnCode || undefined,
          orderedQty: i.qty,
          receivedQty: 0,
          unit: i.stockUnit || 'Pcs',
          enteredUnitId: i.enteredUnitId || undefined,
          rate: i.purRate,
          gstRate: i.gstRate,
          size: i.size || undefined,
          group: i.group || undefined,
          brand: i.brand || undefined,
          articleNo: i.articleNo || undefined,
        })),
      };
    },
    [
      supplier,
      poDate,
      expectedDelivery,
      priority,
      billingAddress,
      deliveryAddress,
      paymentTerms,
      termsConditions,
      narration,
      selectedWarehouseId,
      grandTotal,
      poNumber,
    ],
  );

  // ── Save PO ──────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    console.log('📦 Current items:', JSON.stringify(items, null, 2));

    // ✅ Check supplier
    if (!supplier) {
      toast.error('Please select a supplier');
      return;
    }

    // ✅ Check if any items exist (including new ones without itemId)
    const validItems = items.filter(
      (i) => (i.itemName || i.itemId) && i.qty > 0 && i.purRate > 0,
    );

    if (validItems.length === 0) {
      toast.error('Add at least one item with quantity and rate');
      return;
    }

    setSaving(true);
    try {
      // ✅ Step 1: Create all new items
      const resolvedItems = await resolveNewItems();

      // ✅ Step 2: Check if any items failed to create
      const failedItems = resolvedItems.filter((i) => i.itemName && !i.itemId);

      if (failedItems.length > 0) {
        toast.error(
          `Failed to create items: ${failedItems.map((i) => i.itemName).join(', ')}`,
        );
        setSaving(false);
        return;
      }

      // ✅ Step 3: Build payload with resolved items
      const payload = buildPayload(resolvedItems);
      console.log('📤 PO PAYLOAD:', payload);

      // ✅ Step 4: Create Purchase Order
      const res = await apiCreatePO(payload);
      if (!res.success) {
        toast.error(res.message ?? 'Failed to create purchase order');
        return;
      }

      toast.success(`✅ Purchase Order created! ${res.message || ''}`);

      // ✅ Step 5: Close tab and navigate
      if (activePurchaseTabId) {
        closeTab(activePurchaseTabId);
      }

      navigate('/purchase/orders');
    } catch (err) {
      console.error('❌ Save PO error:', err);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    supplier,
    items,
    resolveNewItems,
    buildPayload,
    toast,
    activePurchaseTabId,
    closeTab,
    navigate,
  ]);

  const handleSaveNew = useCallback(async () => {
    if (!supplier) {
      toast.error('Please select a supplier');
      return;
    }
    const validItems = items.filter(
      (i) => i.itemName && i.qty > 0 && i.purRate > 0,
    );
    if (validItems.length === 0) {
      toast.error('Add at least one item with quantity and rate');
      return;
    }

    setSaving(true);
    try {
      const resolvedItems = await resolveNewItems();
      const payload = buildPayload(resolvedItems);
      const res = await apiCreatePO(payload);
      if (!res.success) {
        toast.error(res.message ?? 'Failed to create purchase order');
        return;
      }
      toast.success(res.message);
      resetForm(
        setSupplier,
        setPoDate,
        setExpectedDelivery,
        setItems,
        setPriority,
        setBillingAddress,
        setTermsConditions,
        setDeliveryAddress,
        setPaymentTerms,
        setNarration,
      );
      if (activePurchaseTabId) {
        setPODraft(activePurchaseTabId, createEmptyPOFormDraft());
      }
    } catch (err) {
      console.error('Save PO error:', err);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    supplier,
    items,
    resolveNewItems,
    buildPayload,
    toast,
    activePurchaseTabId,
  ]);

  const handlePrint = useCallback(() => {
    toast.info('Print feature coming soon');
  }, [toast]);

  return (
    <AppLayout>
      <div className="flex flex-col h-screen overflow-hidden relative z-0">
        <BillingTabBar
          voucherType="PURCHASE"
          onNewTab={handleNewTab}
          onSelectTab={handleSelectTab}
          onCloseTab={handleCloseTab}
        />

        <POHeaderBar
          supplier={supplier}
          onSupplierSelect={handleSupplierSelect}
          poDate={poDate}
          onPoDateChange={setPoDate}
          expectedDelivery={expectedDelivery}
          onExpectedDeliveryChange={setExpectedDelivery}
          poNumber={poNumber}
          gstType={gstType}
        />

        <div className="flex-1 overflow-y-auto bg-[#f8fafc] pb-16">
          <div className="flex items-center justify-between px-5 pt-4 pb-3 bg-white border-b border-[#e2e8f0]">
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-0.5">
                  <span>Purchase</span>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-[#4f46e5] font-semibold">
                    New Purchase Order
                  </span>
                </div>
                <p className="text-xs text-slate-400">{poNumber}</p>
              </div>
              <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                <i className="ri-time-line text-xs" />
                PENDING
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveNew}
                disabled={saving}
                className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f1f5f9] cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
              >
                <i className="ri-save-line text-xs" /> Save &amp; New{' '}
                <kbd className="text-[10px] bg-[#e2e8f0] px-1 rounded ml-0.5">
                  F8
                </kbd>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-3-line" /> Save PO{' '}
                    <kbd className="text-[10px] bg-white/20 px-1 rounded ml-0.5">
                      F9
                    </kbd>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="px-5 pt-4">
            <div className="flex items-center justify-between mb-1.5 px-1">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Item Details
              </span>
              <span className="text-xs text-slate-400">
                <i className="ri-barcode-line mr-1" />
                Scan barcode or type · Enter / Arrow keys to navigate
              </span>
            </div>
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-visible">
              <POItemsTable
                items={items}
                tableRef={tableRef}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                onAddRow={addRow}
                onAddNewItem={handleAddNewItem}
              />
            </div>
          </div>

          <div className="px-5 pt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <POSummaryPanel
              totalItems={totalItems}
              totalQty={totalQty}
              subtotal={subtotal}
              cgst={gstBreakdown.cgst}
              sgst={gstBreakdown.sgst}
              igst={gstBreakdown.igst}
              grandTotal={grandTotal}
              gstType={gstType}
              priority={priority}
              onPriorityChange={setPriority}
            />
            <POSupplierPanel
              supplier={supplier}
              billingAddress={billingAddress}
              onBillingAddressChange={setBillingAddress}
              termsConditions={termsConditions}
              onTermsChange={setTermsConditions}
            />
            <POInfoPanel
              poNumber={poNumber}
              onPoNumberChange={setPoNumber}
              poDate={poDate}
              deliveryAddress={deliveryAddress}
              billingAddress={billingAddress}
              onDeliveryAddressChange={setDeliveryAddress}
              paymentTerms={paymentTerms}
              onPaymentTermsChange={setPaymentTerms}
            />
          </div>

          <div className="px-5 pt-4">
            <div className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 flex items-center gap-3">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide shrink-0">
                Narration
              </label>
              <input
                type="text"
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                placeholder="Add a note for this purchase order..."
                className="flex-1 h-8 px-2 text-sm bg-transparent focus:outline-none text-[#1e293b] placeholder:text-slate-400"
              />
            </div>
          </div>
        </div>

        <ShortcutBar
          onSave={handleSave}
          onSaveNew={handleSaveNew}
          onPrint={handlePrint}
          onBack={() => navigate('/purchase/orders')}
          isDirty={items.some((i) => i.itemName) || !!supplier}
        />
      </div>
    </AppLayout>
  );
}